import React from 'react';
import { useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import {
  findFeaturesByName,
  generateDataLayerStringData,
  getSearchRatParams,
} from '@travel/traveler-core/utils/jrdpHelper';
import isNotEmptyArray from '@travel/ui/utils/arrays';

import Breadcrumb from 'components/BreadCrumb';
import DataLayer from 'components/DataLayer';
import GoToTopButton from 'components/GoToTopButton';
import ItineraryLink from 'components/ItineraryLink';
import ProviderHeaderNavigation from 'components/ProviderHeaderNavigation';
import ProviderInfoHeader from 'components/ProviderInfoHeader';
import HotelFooter from 'pages/PlansList/components/HotelFooter';
import { getPlanListData } from 'store/planList/selectors';
import { getProviderDetails } from 'store/providerDetails/selectors';
import PlanTabSelection from './components/PlanTabSelection';
import { GSP_JRDP_MAPPING } from '@travel/traveler-core/constants/JRDPAnalytics';

import paths from 'constants/paths';
import { getPlaceLayers } from 'utils/search';

import styles from './planList.module.scss';

import type { linkProps, URLSearchQuery } from 'Common-Types';

export type Props = {
  path: string;
  /** String or provider id and page num for seo crawlers in the url */
  params: { pageIdentifier: string; pageNumber: number };
  /** Object of search query */
  query?: URLSearchQuery;
};

const PlanList = (props: Props) => {
  const { id, name, breadcrumb, providerFeatures, reviewScore, totalReviews, totalMediums } =
    useSelector(getProviderDetails);
  const planList = useSelector(getPlanListData);
  const { query } = props;
  const { plans, departureStation, destinationStation } = planList;

  const links: Array<linkProps> = [
    {
      name: <Translate id="Plan_List.Basic_Information.Basic_Information" />,
      url: paths.hotelTop.pathResolver(id, query),
    },
    {
      name: <Translate id="Plan_List.Basic_Information.Plan" />,
      url: '',
    },
    {
      name: <Translate id="Plan_List.Basic_Information_PC.Photo" />,
      url: paths.hotelTop.pathResolver(id, query, 'photo'),
    },
    {
      name: <Translate id="Plan_List.Basic_Information.Review" />,
      url: paths.hotelTopReview.pathResolver(id, query),
    },
  ];

  const { isPC } = useDeviceTypes();
  const specialCategories = findFeaturesByName('SPECIAL_PROVIDER_CATEGORY', providerFeatures);

  const placeLayers = getPlaceLayers(breadcrumb, query);

  return (
    <div data-testid="planList-container" className={styles.wrapper}>
      {isPC && <Breadcrumb placeLayers={placeLayers} className={styles.breadcrumb} />}
      <ItineraryLink query={props.query} title={<Translate id="Plan_List.Change_Plan.Title" />} />
      <ProviderInfoHeader
        providerId={id}
        providerName={name}
        providerFeatures={providerFeatures}
        reviewScore={reviewScore}
        totalReviews={totalReviews}
        query={query}
      />
      <ProviderHeaderNavigation
        providerId={id}
        totalReviews={totalReviews}
        totalMediums={totalMediums}
        className={styles.headerNavigation}
        query={query}
      />
      <PlanTabSelection
        {...props}
        planList={planList}
        departureStation={departureStation?.name}
        destinationStation={destinationStation?.name}
      />
      {!isPC && <ItineraryLink query={props.query} />}
      {!isPC && (
        <>
          <Breadcrumb placeLayers={placeLayers} className={styles.breadcrumb} />
          {isNotEmptyArray(plans) && <GoToTopButton className={styles.goToTopButton} />}
          <HotelFooter links={links} hotelName={name} />
        </>
      )}
      {((departureStation?.code && destinationStation?.code) ||
        !isNotEmptyArray(planList?.plans)) && (
        <DataLayer
          page="plan_list"
          {...getSearchRatParams(props.query)}
          providerIds={id}
          providerName={name}
          itemIds={generateDataLayerStringData(plans, 'planId')}
          providerCategories={generateDataLayerStringData(specialCategories, 'id')}
          isBookable={isNotEmptyArray(plans) ? 'bookable' : 'unbookable'}
          stepParam="step2"
          cartModifyPlanList={props.query?.transactionId ? 1 : ''}
          departureStation={departureStation?.code}
          destinationStation={destinationStation?.code}
          gspRequestId={planList.gspRequestId}
          planListGsp={GSP_JRDP_MAPPING[planList.searchEngine]}
        />
      )}
    </div>
  );
};

export default PlanList;
